/* import __COLOCATED_TEMPLATE__ from './settings.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type HandoffToSalesforceCaseType from 'embercom/models/operator/visual-builder/step/handoff-to-salesforce-case';
import { SalesforceCaseDescriptionSource } from 'embercom/models/operator/visual-builder/step/handoff-to-salesforce-case';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type IntlService from 'ember-intl/services/intl';
import type {
  HandoffOption,
  HandoffOptionGroup,
} from 'embercom/components/workflows/graph-editor/node-items/steps/handoff-to-salesforce-case';
import { BlocksDocument } from '@intercom/composer-ember';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { StandaloneComposerConfig } from 'embercom/objects/standalone/composer-config';
import AttributeInfoResolver from 'embercom/lib/common/template-attribute-resolver';
import type IntercomAttribute from 'embercom/models/attribute';
import type { Block } from 'embercom/models/common/blocks/block';
import type RouterService from '@ember/routing/router-service';
import { type SafeString } from '@ember/template/-private/handlebars';
import type HandoffToSalesforceTeamType from 'embercom/models/operator/visual-builder/step/handoff-to-salesforce-team';
import { isEmpty, isPresent } from '@ember/utils';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import type { SalesforceAdmin, SalesforceQueue } from 'embercom/objects/standalone/salesforce';
import type FinStepType from 'embercom/models/operator/visual-builder/step/fin';
import type HandoffToExternalTicketOnResolutionData from 'embercom/models/operator/fin/handoff-to-external-ticket-on-resolution-data';

interface Signature {
  Args: {
    isReadOnly: boolean;
    isLoading: boolean;
    className?: string;
    step: HandoffToSalesforceCaseType | HandoffToSalesforceTeamType | FinStepType;
    isHandoffToSalesforceCase: boolean;
    bannerHeading: string;
    caseDescriptionSource?: SalesforceCaseDescriptionSource;
    setCaseDescriptionSource: (source: SalesforceCaseDescriptionSource) => void;
    caseSubjectBlocks?: BlocksDocument;
    onCaseSubjectChange?: (value: BlocksDocument) => void;
    caseDescriptionBlocks?: BlocksDocument;
    onCaseDescriptionChange?: (value: BlocksDocument) => void;
  };
}

type StepConfig =
  | HandoffToSalesforceCaseType
  | HandoffToSalesforceTeamType
  | HandoffToExternalTicketOnResolutionData;

const EXCLUDED_ATTRIBUTES = [
  'fin_preview',
  'ai_tone_of_voice',
  'ai_answer_length',
  'ai_language_style',
  'ai_chatbot_resolution_state',
  'fin_actions_used',
  'standalone_zendesk_brand',
  'ai_insights_topic',
  'ai_insights_subtopic',
  'ai_insights_topic_subtopic',
  'ai_insights_conversation_summary',
];

export interface SelectedHandoverOptionByIntegrationId {
  [integrationId: string]: HandoffOption | undefined;
}

export default class Settings extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare attributeService: $TSFixMe;
  @service declare router: RouterService;

  @tracked caseSubjectBlocksDoc: BlocksDocument;
  @tracked caseDescriptionBlocksDoc: BlocksDocument;
  caseSubjectComposerConfig: StandaloneComposerConfig;
  caseDescriptionComposerConfig: StandaloneComposerConfig;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.caseSubjectBlocksDoc = args.caseSubjectBlocks || new BlocksDocument([]);
    this.caseDescriptionBlocksDoc = args.caseDescriptionBlocks || new BlocksDocument([]);

    let attributeResolver = new AttributeInfoResolver({
      attributes: this.appService.app.allowedAttributes,
      attributeAllowlist: this.attributeService.attributesToGroupList(this.allowedAttributes),
      conversationAttributes: this.workflowUsableConversationAttributes,
      includeAppAttributes: false,
    });

    this.caseSubjectComposerConfig = new StandaloneComposerConfig({
      resolver: attributeResolver,
      autoFocus: false,
    });

    this.caseDescriptionComposerConfig = new StandaloneComposerConfig({
      resolver: attributeResolver,
      singleBlockMode: false,
      autoFocus: false,
    });
  }

  get app() {
    return this.appService.app;
  }

  get platform() {
    return this.finStandaloneService.salesforcePlatform;
  }

  get step() {
    return this.args.step;
  }

  get hasManyConfigurations() {
    return this.platform.configurations!.length > 1;
  }

  get allowedAttributes() {
    return this.attributeService.userEditorMetadataAndConversationAttributes.reject(
      (attr: IntercomAttribute) =>
        attr.isUserEvent || attr.isCompany || EXCLUDED_ATTRIBUTES.includes(attr.cdaId || ''),
    );
  }

  get stepConfig(): StepConfig {
    if ('handoffToExternalTicketOnResolutionData' in this.step) {
      return this.step.handoffToExternalTicketOnResolutionData;
    }

    return this.step;
  }

  /**
   * Returns conversation attributes filtered to exclude Salesforce fields that are
   * neither createable nor updateable (e.g., "Case Number"). These read-only system
   * fields cannot be set in workflows and should not be available for templating.
   */
  get workflowUsableConversationAttributes() {
    let conversationAttributes =
      this.attributeService.nonArchivedConversationCustomAttributesWithoutFile;

    // If no Salesforce platform, return all conversation attributes
    if (!this.finStandaloneService.salesforcePlatform) {
      return conversationAttributes;
    }

    // Filter out conversation attributes that are mapped to non-writable Salesforce fields
    return conversationAttributes.filter((attribute: $TSFixMe) => {
      // Check if this conversation attribute is mapped to a Salesforce field
      let salesforceField =
        this.finStandaloneService.salesforcePlatform.findMappedConversationField(
          attribute.identifier,
        );

      if (!salesforceField) {
        // Not a Salesforce field, include it
        return true;
      }

      // Include only if the field is createable or updateable
      return salesforceField.createable || salesforceField.updateable;
    });
  }

  get hasSubjectContent() {
    return this.caseSubjectBlocksDoc.blocks.some(
      (block: Block) => block.text && block.text.trim().length > 0,
    );
  }

  get hasDescriptionContent() {
    return this.caseDescriptionBlocksDoc.blocks.some(
      (block: Block) => block.text && block.text.trim().length > 0,
    );
  }

  @action
  handoverOptions(config: SalesforceConfiguration): HandoffOptionGroup[] {
    let options = [];
    let handoverOptions = config.handoverOptions;

    if (isPresent(handoverOptions.queues)) {
      options.push({
        heading: this.intl.t(
          'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.queues-title',
        ),
        items: handoverOptions.queues.map((item: SalesforceQueue) => ({
          text: item.name,
          value: item.id,
          componentClasses: 'twk__standalone-dropdown-item',
        })),
      });
    }

    if (isPresent(handoverOptions.admins)) {
      options.push({
        heading: this.intl.t(
          'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.users-title',
        ),
        items: handoverOptions.admins.map((item: SalesforceAdmin) => ({
          text: item.name,
          value: item.id,
          componentClasses: 'twk__standalone-dropdown-item',
        })),
      });
    }

    if (isEmpty(options)) {
      options = [this.noQueuesOrUsersPlaceholderGroupOption];
    } else if (this.args.isHandoffToSalesforceCase) {
      options = [this.unassignedGroupOption, ...options];
    }

    return options;
  }

  private get noQueuesOrUsersPlaceholderGroupOption(): HandoffOptionGroup {
    return {
      heading: '',
      items: [
        {
          text: this.intl.t(
            'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.no-queues-or-users-available',
          ),
          value: '',
          isDisabled: true,
        },
      ],
    };
  }

  private get unassignedGroupOption(): HandoffOptionGroup {
    return {
      heading: '',
      items: [
        {
          text: this.intl.t(
            'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.unassigned-owner-label',
          ),
          value: null,
        },
      ],
    };
  }

  get salesforceConfig() {
    return this.finStandaloneService.salesforcePlatform.salesforceConfig!;
  }

  get salesforcePlatform() {
    return this.finStandaloneService.salesforcePlatform;
  }

  get isDigitalExperiencesEnabled() {
    return this.salesforcePlatform?.salesforceSetupData?.hasDigitalExperiences ?? false;
  }

  get digitalExperiencesSettingsUrl() {
    return this.salesforceConfig.buildLightningSetupUrl('NetworkSettings/home');
  }

  get caseDescriptionSource(): SalesforceCaseDescriptionSource {
    return this.args.caseDescriptionSource || SalesforceCaseDescriptionSource.TEXT;
  }

  get handoffToSalesforceCaseStep(): HandoffToSalesforceCaseType | undefined {
    if ('caseRecordTypeId' in this.step) {
      return this.step;
    }
    return undefined;
  }

  get defaultLabelForCaseOwner(): string {
    return this.args.isHandoffToSalesforceCase
      ? this.intl.t(
          'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.unassigned-owner-label',
        )
      : this.intl.t(
          'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.select-agent-or-queue-default-label',
        );
  }

  get handoffToTeamCaseDataSteps(): { text: SafeString }[] {
    let steps = [
      {
        text: this.intl.t(
          'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.case-data-for-handoff-to-team.step-1',
          {
            htmlSafe: true,
          },
        ),
      },
    ];

    if (this.app.canUseFinFirstAiCategories) {
      steps.push({
        text: this.intl.t(
          'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.case-data-for-handoff-to-team.step-2-fin-first-ai-categories',
          {
            aiCategoriesUrl: this.router.urlFor('apps.app.standalone.attributes'),
            htmlSafe: true,
          },
        ),
      });
    } else {
      steps.push({
        text: this.intl.t(
          'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.case-data-for-handoff-to-team.step-2-category-detection',
          {
            htmlSafe: true,
          },
        ),
      });
    }

    return steps as { text: SafeString }[];
  }

  get selectedHandoverOption(): SelectedHandoverOptionByIntegrationId {
    let selectedHandoverOptions: SelectedHandoverOptionByIntegrationId = {};

    this.platform?.configurations?.forEach((integration) => {
      let selectedOwnerForIntegration =
        this.stepConfig.handoffToSalesforceOwnerIdByIntegration?.find(
          (owner: { integration_id: string; handoff_id: string }) =>
            owner.integration_id === integration.id,
        );

      let ownerForIntegration: string | null | undefined;
      if (selectedOwnerForIntegration) {
        ownerForIntegration = selectedOwnerForIntegration.handoff_id;
      } else if (
        this.stepConfig.handoffToSalesforceOwnerId &&
        integration.findHandoverOptionById(this.stepConfig.handoffToSalesforceOwnerId)
      ) {
        ownerForIntegration = this.stepConfig.handoffToSalesforceOwnerId;
      }

      selectedHandoverOptions[integration.id] = this.handoverOptions(integration)
        .flatMap((group) => group.items)
        .find((option) => option.value === ownerForIntegration);
    });

    return selectedHandoverOptions;
  }

  @action
  onCaseSubjectChange(document: BlocksDocument) {
    this.caseSubjectBlocksDoc = document;
    this.args.onCaseSubjectChange?.(document);
  }

  @action
  onCaseDescriptionChange(document: BlocksDocument) {
    this.caseDescriptionBlocksDoc = document;
    this.args.onCaseDescriptionChange?.(document);
  }

  @action
  setHandoffToSalesforceOwnerId(integrationId: string, handoffId: string) {
    let legacyHandoffId = this.stepConfig.handoffToSalesforceOwnerId;
    let handoffByIntegration = [...(this.stepConfig.handoffToSalesforceOwnerIdByIntegration ?? [])];

    // Migrate legacy handoff to the new format
    if (legacyHandoffId) {
      this.platform?.configurations?.forEach((integration) => {
        if (handoffByIntegration.some((entry) => entry.integration_id === integration.id)) {
          return;
        }

        if (integration.findHandoverOptionById(legacyHandoffId)) {
          handoffByIntegration.push({
            integration_id: integration.id,
            handoff_id: legacyHandoffId,
          });
        }
      });
    }

    handoffByIntegration = handoffByIntegration.filter(
      (entry) => entry.integration_id !== integrationId,
    );

    handoffByIntegration.push({
      integration_id: integrationId,
      handoff_id: handoffId,
    });

    this.stepConfig.handoffToSalesforceOwnerIdByIntegration = handoffByIntegration;

    if (!this.hasManyConfigurations) {
      this.stepConfig.handoffToSalesforceOwnerId = handoffId;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::HandoffToSalesforceCase::Settings': typeof Settings;
    'workflows/graph-editor/node-items/steps/handoff-to-salesforce-case/settings': typeof Settings;
  }
}
